// packages/auth/src/role.ts

/**
 * The user types Annex A story 1 enumerates.
 *
 * `PRS_SERVICE` is in the same list deliberately: the story names it alongside the
 * human roles, and giving a machine caller a separate mechanism is how it ends up
 * outside the audit trail. It is an identity like any other; what differs is its
 * scope.
 */
export const ROLES = [
  'HSP_USER',
  'MBIM_USER',
  'ENTITLEMENT_USER',
  'RECOVERY_USER',
  'PAYMENT_USER',
  'MINISTRY_HR_USER',
  'PSD_USER',
  'MMBA_USER',
  'PRS_SERVICE',
  /**
   * ADR 0013 — the one role that manages PRS2's own user accounts (who may sign in,
   * with which role and scope). It holds NO bill grant and no reference-data right:
   * it administers who has access, never what the access is used for, so a person
   * cannot both grant themselves PSD authority and use it. It is a person, not a
   * machine (see {@link MACHINE_ROLES}).
   */
  'SYSTEM_ADMIN',
  /**
   * Phase 8 (Annex A 52-59) — GovTech's Central Accounts Management service,
   * the caller of the eight CAM-consumed endpoints (P2C3 §1.1.1(b)).
   *
   * ⚠ **Never a row in `app.app_user`.** Every other member of this union is a
   * PRS2 staff account story 1 enumerates (`002_identity.sql`'s own CHECK
   * constraint), including `PRS_SERVICE`, which IS seeded there so it can hold
   * `app.user_scope` grants. CAM is an EXTERNAL GovTech system authenticating
   * with its own shared credential (`services/gateway/src/cam-auth.ts`'s
   * `requireCamAgent`), never a signed-in officer, and needs no bill grant —
   * only this label, so a CAM-attributed audit row (`actorRole`) reads
   * `CAM_AGENT` rather than `ANONYMOUS`.
   */
  'CAM_AGENT',
] as const
export type Role = (typeof ROLES)[number]

export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  HSP_USER: 'Healthcare Service Provider',
  MBIM_USER: 'MBIM Officer',
  ENTITLEMENT_USER: 'Entitlement Agency Officer',
  RECOVERY_USER: 'Recovery Agency Officer',
  PAYMENT_USER: 'Payment Agency Officer',
  MINISTRY_HR_USER: 'Ministry / Agency HR',
  PSD_USER: 'Public Service Division',
  MMBA_USER: 'MMBA Officer',
  PRS_SERVICE: 'PRS Service (machine)',
  SYSTEM_ADMIN: 'System Administrator',
  CAM_AGENT: 'CAM (GovTech Central Accounts Management)',
}

/**
 * ★★ THE IDENTITIES WITH NO PERSON BEHIND THEM.
 *
 * `grant-analysis.ts` already reasons about *"the two MACHINE identities"* — and
 * had to, because they are the two roles that hold none of the three
 * self-service capabilities (`mayUseHelpAndManual`, `mayUseFaq`,
 * `mayRaiseSupportTicket`) the other eight hold. That measurement is where this
 * set comes from; it is not a new opinion about which roles are machines.
 *
 * ⚠ **A `ReadonlySet<Role>` rather than a predicate over a name pattern.**
 * `PRS_SERVICE` and `CAM_AGENT` share no naming convention — one ends `_SERVICE`
 * and the other `_AGENT`, while every human role ends `_USER` except neither of
 * these — so a regular expression over the identifier would be a rule invented
 * here that the next role could quietly fall outside of. `role.test.ts` asserts
 * every member of {@link ROLES} is classified one way or the other, so adding a
 * role forces a decision instead of inheriting "human" by omission.
 *
 * ⚠ **What this is FOR, and the one thing it must never become.** It labels an
 * identity on screen so a reader can see that nobody is behind it — Annex A
 * story 1 lists `PRS_SERVICE` alongside the human roles deliberately (see
 * {@link ROLES}), and `docs/security/SECURITY.md` **S-070** records that
 * `POST /api/session` applies no role filter, so the identity picker will hand a
 * human the machine's authority. **This set is not, and must not become, that
 * filter.** ADR 0012 decided to NARROW the machine's grants; the repository
 * owner deferred applying that until after the demonstration so the product's
 * own access-control matrix reports the over-privilege live. Hiding the identity
 * would delete the finding rather than fix it.
 */
export const MACHINE_ROLES: ReadonlySet<Role> = new Set<Role>(['PRS_SERVICE', 'CAM_AGENT'])

/** Is this role an unattended machine caller rather than a person? See {@link MACHINE_ROLES}. */
export function isMachineRole(role: Role): boolean {
  return MACHINE_ROLES.has(role)
}
